import React, { useContext, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../../PageRouting/approutes";
import { VoiceCommandContext } from "./VoiceContext";
import dayjs from "dayjs";
import { parseDate } from "chrono-node";
import { Content } from "antd/es/layout/layout";
import { getVoiceValueFromTrigger } from "./getVoiceValueFromTrigger";

const VoiceCommandController = () => {
  const location = useLocation();
  const isRecognitionRunningRef = useRef(false);
  


  const {
    isListening,
    setBpValue,
    setSearchPatientName,
    recognitionRef,
    setIsListening,
    setChiefComplaintVoice,
    setSymptomsVoice,
    setDiseaseConditionVoice,
    setIcdCodeVoice,
    activeSection,
    setActiveSection,
    liveChiefComplaint,
    setLiveChiefComplaint,
    setVitals,
    setMedicineNameVoice,
    setDosageVoice,
    setDurationVoice,
    setquantityVoice,
    setNotesVoice,
    medicineData,
    setMedicineData,
    setMedicineVoice,
    setLabTestVoice,
    setTreatmentPlanVoice,
    setSchedule,
    setIsPreviewModal,
    setMedicalOpen,
    setSelectedOption,
    setDeleteMedicineVoice,
    setDeleteLabTestVoice,
    setDeleteDiagnosisVoice,
    setIsMicActive,
    setEditMedicineVoice,
    setSelectAllVoice,

  } = useContext(VoiceCommandContext);

  const activeSectionRef = useRef(activeSection);

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);
  

  useEffect(() => {
    if (!recognitionRef.current) return;

    const recognition = recognitionRef.current;

    const handleVoiceFollowUpDate = (transcript) => {
      const cleaned = transcript
        .toLowerCase()
        .replace("follow up", "")
        .replace("next visit", "")
        .replace("next consultation on", "")
        .replace("consult me on", "")
        .replace("set follow up on", "")
        .trim();

      const parsed = parseDate(cleaned);

      if (parsed) {
        const parsedDay = dayjs(parsed);
        const today = dayjs().startOf("day");

        if (parsedDay.isBefore(today)) {
          console.warn("Ignored past follow-up date:", parsedDay.format("DD/MM/YYYY"));
          message.warning("Past date is not allowed", parsedDay.format("DD/MM/YYYY"))
          return; // 🚫 Don't set past dates
        }

        setSchedule(parsedDay); // ✅ Safe to set
      } else {
        console.warn("Could not parse follow-up date from:", transcript);
      }
    };

  
   
    const dosageVoiceMap = {
      "night only": "0-0-1",
      "afternoon only": "0-1-0",
      "morning only": "1-0-0",
      "morning and night": "1-0-1",
      "morning and afternoon": "1-1-0",
      "3 times a day": "1-1-1",
      "4 times a day": "1-1-1-1",
      "morning and night only": "1-0-0-1",
      "morning only long": "1-0-0-0",
      "morning and afternoon only": "1-1-0-0",
      "morning, afternoon and evening": "1-1-1-0",
      "night alone": "0-0-0-1",
      "afternoon and night": "0-0-1-1",
      "afternoon alone": "0-0-1-0",
      "afternoon only long": "0-1-0-0",
      "afternoon, evening and night": "0-1-1-1",
      "once when needed": "s.o.s",
      "once a week": "q.wk.",
      "twice daily": "b.i.d.",
      "3 times daily": "t.i.d.",
      "4 times daily": "q.i.d.",
      "at bedtime": "q.h.s.",
      "5 times a day": "5X a day",
      "every 4 hours": "q.4h",
      "every 6 hours": "q.6h",
      "once daily": "q.0.d.",
      "as needed": "prn.",
      "daily": "q.d.",
      "intramuscular": "I.M.",
      "subcutaneous": "Subq.",
      "rectal": "Rectally",
      "intravenous": "I.V.",
      "right eye": "O.D.",
      "left eye": "O.S.",
      "both eyes": "O.U.",
      "2ml": "2 ml",
      "2 ml": "2 ml",
      "2.5ml": "2.5 ml",
      "2.5 ml": "2.5 ml",
      "5ml": "5 ml",
      "5 ml": "5 ml",
      "10ml": "10 ml",
      "10 ml": "10 ml"
    };

    const frequencyVoiceMap = {
      "2ml": "2 ml",
      "2 ml": "2 ml",
      "2.5ml": "2.5 ml",
      "2.5 ml": "2.5 ml",
      "5ml": "5 ml",
      "5 ml": "5 ml",
      "10ml": "10 ml",
      "10 ml": "10 ml",
      "1": "1",
      "half": "1/2",
      "quarter": "1/4",
      "3 quarter": "3/4"
    }

    const numberWordsMap = {
      zero: "0", one: "1", two: "2", three: "3", four: "4", five: "5",
      six: "6", seven: "7", eight: "8", nine: "9", ten: "10",
    };

    const normalizeTranscript = (text) => {
      return text
        .toLowerCase()
        .split(" ")
        .map((word) => numberWordsMap[word] || word)
        .join(" ")
        .trim();
    };

    recognition.onstart = () => {
      isRecognitionRunningRef.current = true;
      setIsMicActive(true); // 🔥 Mic is now active
      message.success("🎙️ Your mic is ON");
    };
    
    recognition.onend = () => {
      isRecognitionRunningRef.current = false;
      setIsMicActive(false); // 🔥 Mic is no longer active
      message.warning("🎙️ Your mic is OFF");
      if (isListening) {
        try {
          recognition.start();
        } catch (e) {
          console.warn("Recognition already running or failed:", e);
        }
      }
    };


    recognition.onresult = (event) => {
      const lastResult = event.results[event.results.length - 1];
      if (!lastResult.isFinal) return; // ✅ Skip interim results
    
      let transcript = lastResult[0].transcript.toLowerCase().trim();
      const currentSection = activeSectionRef.current;

      // Full stop cleanup logic
      if (transcript.match(/\d+(\.\d+)?/)) {
        transcript = transcript.replace(/[\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
      } else {
        transcript = transcript.replace(/[.\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
      }
    
      // message.warning(transcript)
      message.open({
        content: transcript,
        icon: null,
      });
      const normalizedTranscript = normalizeTranscript(transcript); // if needed
   

      if (location.pathname === APP_ROUTES.VIDEOCALL) {
        const current = activeSectionRef.current;

  if (transcript.includes("consultation notes") && current !== "consultationNotes") {
    setActiveSection("consultationNotes");
    message.success("Switched to Consultation Notes");
    return;
  } 
  if (transcript.includes("specialty notes") && current !== "specialtyNotes") {
    setActiveSection("specialtyNotes");
    message.success("Switched to Specialty Notes");
    return;
  } 
  if (transcript.includes("medicine") && current !== "addMedicines") {
    setActiveSection("addMedicines");
    message.success("Switched to Medicines");
    return;
  } 
  if (transcript.includes("lab test") && current !== "addLabTest") {
    setActiveSection("addLabTest");
    message.success("Switched to Lab Tests");
    return;
  } 
  if (transcript.includes("treatment plan") && current !== "treatmentPlan") {
    setActiveSection("treatmentPlan");
    message.success("Switched to Treatment Plan");
    return;
  }
       

        else if (transcript.includes("height")) {
          const match = transcript.match(/\d+(\.\d+)?/);
          if (match) {
            setVitals((prev) => ({ ...prev, height: match[0] }));
            message.success(`Height set: ${match[0]} Ht`);
          }
        }
        else if (transcript.includes("weight")) {
          const match = transcript.match(/\d+(\.\d+)?/);
          if (match) {
            setVitals((prev) => ({ ...prev, weight: match[0] }));
            message.success(`Weight set: ${match[0]} Ht`);
          }
        }
        else if (transcript.includes("bp")) {
          // Clean up punctuation including trailing full stop
          const cleanedTranscript = transcript
            .replace(/[.\/#!$%\^&\*;:{}=\-_`~()]/g, "")
            .trim();

          // Support "bp 120 over 80" or "bp 120 slash 80" or "bp 120 80"
          const matches =
            cleanedTranscript.match(/(\d+)\s*(?:over|\/|\s)\s*(\d+)/);

          if (matches) {
            const [_, sys, dia] = matches;
            setVitals((prev) => ({
              ...prev,
              systolic: sys,
              diastolic: dia,
            }));
            message.success(`BP set: ${sys}/${dia}`);
          }
        }

        else if (transcript.includes("heart rate") || transcript.includes("hr")) {
          const match = transcript.match(/\d+(\.\d+)?/);
          if (match) {
            setVitals((prev) => ({ ...prev, heartRate: match[0] }));
            message.success(`Heart Rate set: ${match[0]} bpm`);
          }
        } else if (transcript.includes("temperature") || transcript.includes("temp")) {
          const match = transcript.match(/\d+(\.\d+)?/);
          if (match) {
            setVitals((prev) => ({ ...prev, temperature: match[0] }));
            message.success(`Temperature set: ${match[0]} °F`);
          }
        } else if (transcript.includes("sp o2") || transcript.includes("oxygen")) {
          const match = transcript.match(/\d+(\.\d+)?/);
          if (match) {
            setVitals((prev) => ({ ...prev, spo2: match[0] }));
            message.success(`SpO2 set: ${match[0]} %`);
          }
        } else if (transcript.includes("respiration") || transcript.includes("rr")) {
          const match = transcript.match(/\d+(\.\d+)?/);
          if (match) {
            setVitals((prev) => ({ ...prev, rr: match[0] }));
            message.success(`Respiratory Rate set: ${match[0]}`);
          }
        }

        else if (transcript.includes("open medical record")) {
          setMedicalOpen(true);
        }
        const lowerTranscript = transcript.toLowerCase();

        if (lowerTranscript.includes("medical history")) {
          setSelectedOption("Medical history");
        } else if (lowerTranscript.includes("prescription")) {
          setSelectedOption("Prescription");
        } else if (lowerTranscript.includes("vitals")) {
          setSelectedOption("Vitals");
        } else if (lowerTranscript.includes("reports")) {
          setSelectedOption("Reports");
        }

        else if (transcript.includes("close medical record")) {
          setMedicalOpen(false);
        }
        // "today"
        // "tomorrow"
        // "next Monday"
        // "in 2 days"
        // "in 1 month"
        if (transcript.toLowerCase().includes("follow up date")) {
          handleVoiceFollowUpDate(transcript);
        }

        else if (transcript.includes("refer")) {
          document.getElementById("refer")?.click();

        }
        else if (transcript.includes("preview")) {
          document.getElementById("preview")?.click();

        }
        else if (transcript.includes("close preview")) {
          setIsPreviewModal(false);
        }


        // if (currentSection === "consultationNotes") {

        //   if (
        //     transcript.startsWith("delete diagnosis") ||
        //     transcript.startsWith("remove diagnosis") ||
        //     transcript.startsWith("delete disease") ||
        //     transcript.startsWith("remove disease")
        //   ) {
        //     const name = transcript
        //       .replace("delete diagnosis", "")
        //       .replace("remove diagnosis", "")
        //       .replace("delete disease", "")
        //       .replace("remove disease", "")
        //       .trim();
        //     setDeleteDiagnosisVoice(name);
        //     message.success(`Delete request for diagnosis: ${name}`);
        //     return;
        //   }
        //   if (transcript.includes("clear chief complaint")) {
        //     setChiefComplaintVoice("__clear__");
        //     setTimeout(() => setChiefComplaintVoice(null), 100); // clear signal
        //     message.success(`Cleared Chief Complaint `);
        //   } else if (transcript.includes("chief complaint")) {
        //     const complaint = transcript.replace("chief complaint", "").trim();
        //     setChiefComplaintVoice(complaint);
        //     message.success(`Chief Complaint set: ${complaint}`);
        //   }
          
        //   if (transcript.includes("clear symptoms")) {
        //     setSymptomsVoice("__clear__");
        //     setTimeout(() => setSymptomsVoice(null), 100); // clear signal
        //     message.success(`Symptoms Cleared`);
        //   } else if (transcript.includes("symptoms")) {
        //     const sym = transcript.replace("symptoms", "").trim();
        //     setSymptomsVoice(sym);
        //     message.success(`Symptom set: ${sym}`);
        //   }

        //   else if (transcript.includes("primary")) {
        //     document.getElementById("Primary")?.click();
        //     message.success(`Primary`);
        //   }
        //   else if (transcript.includes("secondary")) {
        //     document.getElementById("Secondary")?.click();
        //     message.success(`Secondary`);
        //   }
        //   else if (transcript.includes("disease")) {
        //     const disease = transcript.replace("disease", "").trim();
        //     setDiseaseConditionVoice(disease);
        //     message.success(`Disease Condition set: ${disease}`);
        //   } else if (transcript.includes("icd code")) {
        //     const code = transcript.replace("icd code", "").trim();
        //     setIcdCodeVoice(code);
        //     message.success(`ICD Code set: ${code}`);
        //   }
        //   else if (transcript.includes("add diagnosis")) {
       //     document.getElementById("addDiagnosisBtn")?.click();
        //     message.success(`Diagnosis Added`);
        //   }
        //   if (transcript.includes("select all symptom") || transcript.includes("select all symptoms")) {
        //     setSelectAllVoice("symptoms");
        //     setSymptomsVoice(""); // ⛔ prevent typing
        //     return;
        //   }

        //   if (transcript.includes("select all diagnosis")) {
        //     setSelectAllVoice("diagnosis");
        //     setDiseaseConditionVoice(""); // ⛔ prevent ICD search
        //     return;
        //   }
        // }
          if (currentSection === "consultationNotes") {
  const lowerTranscript = transcript.toLowerCase();

  // ✅ Diagnosis Deletion
  const deleteDiagnosisTriggers = [
    "delete diagnosis",
    "remove diagnosis",
    "delete disease",
    "remove disease"
  ];
  const diagnosisToDelete = getVoiceValueFromTrigger(lowerTranscript, deleteDiagnosisTriggers);
  if (diagnosisToDelete !== null) {
    setDeleteDiagnosisVoice(diagnosisToDelete);
    message.success(`Delete request for diagnosis: ${diagnosisToDelete}`);
    return;
  }

  // ✅ Chief Complaint - Clear
  if (lowerTranscript.includes("clear chief complaint")) {
    setChiefComplaintVoice("__clear__");
    setTimeout(() => setChiefComplaintVoice(null), 100);
    message.success(`Cleared Chief Complaint`);
  } else {
    // ✅ Chief Complaint - Add
    const chiefTriggers = [
      "chief complaint",
      "chief complaints",
      "my chief complaint is",
      "chief complaint is"
    ];
    const complaint = getVoiceValueFromTrigger(lowerTranscript, chiefTriggers);
    if (complaint !== null) {
      setChiefComplaintVoice(complaint);
      message.success(`Chief Complaint set: ${complaint}`);
    }
  }

  // ✅ Symptoms - Clear
  if (lowerTranscript.includes("clear symptoms")) {
    setSymptomsVoice("__clear__");
    setTimeout(() => setSymptomsVoice(null), 100);
    message.success("Symptoms Cleared");
  } else {
    // ✅ Symptoms - Add
    const symptomTriggers = [
      "symptom",
      "symptoms",
      "symptoms are",
      "next symptom",
      "s"
    ];
    const symptoms = getVoiceValueFromTrigger(lowerTranscript, symptomTriggers);
    if (symptoms !== null) {
      setSymptomsVoice(symptoms);
      message.success(`Symptom set: ${symptoms}`);
    }
  } 

  // ✅ Primary/Secondary button click
  if (lowerTranscript.includes("primary")) {
    document.getElementById("Primary")?.click();
    message.success(`Primary`);
  } else if (lowerTranscript.includes("secondary")) {
    document.getElementById("Secondary")?.click();
    message.success(`Secondary`);
  }

  // ✅ Disease Condition
  const diseaseTriggers = ["disease", "disease is", "diagnosis", "diagnosis is" ,"diagnosed by","diagnosed"];
  const disease = getVoiceValueFromTrigger(lowerTranscript, diseaseTriggers);
  if (disease !== null) {
    setDiseaseConditionVoice(disease);
    message.success(`Disease Condition set: ${disease}`);
  }

  // ✅ ICD Code
  const icdTriggers = ["icd code", "icd code is", "icd is"];
  const icdCode = getVoiceValueFromTrigger(lowerTranscript, icdTriggers);
  if (icdCode !== null) {
    setIcdCodeVoice(icdCode);
    message.success(`ICD Code set: ${icdCode}`);
  }

  // ✅ Add Diagnosis
  if (lowerTranscript.includes("add diagnosis")) {
    document.getElementById("addDiagnosisBtn")?.click();
    message.success(`Diagnosis Added`);
  }

  // ✅ Select All Symptoms/Diagnosis
  if (
    lowerTranscript.includes("select all symptom") ||
    lowerTranscript.includes("select all symptoms")
  ) {
    setSelectAllVoice("symptoms");
    setSymptomsVoice(""); // prevent typing
    return;
  }

  if (lowerTranscript.includes("select all diagnosis")) {
    setSelectAllVoice("diagnosis");
    setDiseaseConditionVoice(""); // prevent ICD typing
    return;
  }
}


        if (currentSection === "addMedicines") {

          if (
            transcript.startsWith("delete medicine") ||
            transcript.startsWith("remove medicine")
          ) {
            const name = transcript
              .replace("delete medicine", "")
              .replace("remove medicine", "")
              .trim();
            setDeleteMedicineVoice(name);
            message.success(`Delete request for: ${name}`);
            return;
          }

          if (transcript.includes("medicine name") &&
            !transcript.includes("notes") &&
            !transcript.includes("add medicine")) {
            transcript = transcript.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
            const medName = transcript.replace("medicine name ", "").trim();
            setMedicineVoice(medName);
            message.success(`Medicine voice command: ${medName}`);
          }
          if (transcript.includes("composition")) {
            const comp = transcript.replace("composition", "").trim();
            setMedicineData((prev) => ({
              ...prev,
              composition: comp,
            }));
            message.success(`Composition set: ${comp}`);
          }


          if (normalizedTranscript.startsWith("dosage")) {
            const spokenDosage = normalizedTranscript.replace("dosage", "").replace(/[.?!]$/, "").trim();
            const mapped = dosageVoiceMap[spokenDosage];
            if (mapped) {
              setMedicineData(prev => ({ ...prev, dosage: mapped }));
              message.success(`Dosage set: ${mapped}`);
            } else {
              message.warning(`Unrecognized dosage phrase: ${spokenDosage}`);
            }
          }

          if (normalizedTranscript.startsWith("frequency")) {
            const spokenFrequency = normalizedTranscript.replace("frequency", "").replace(/[.?!]$/, "").trim();
            const mapped = frequencyVoiceMap[spokenFrequency];
            if (mapped) {
              setMedicineData(prev => ({ ...prev, frequency: mapped }));
              message.success(`Frequency set: ${mapped}`);
            } else {
              message.warning(`Unrecognized Frequency phrase: ${spokenFrequency}`);
            }
          }

          // Duration
          if (transcript.includes("duration")) {
            const match = transcript.match(/\d+/); // ✅ Extract only numbers
            if (match) {
              setMedicineData(prev => ({ ...prev, duration: match[0] }));
              message.success(`Duration set: ${match[0]} days`);
            } else {
              message.warning("Please speak a valid number for Duration");
            }
          }
          if (transcript.includes("quantity")) {
            const match = transcript.match(/\d+/);
            if (match) {
              setMedicineData(prev => ({ ...prev, quantity: match[0] }));
              message.success(`Quantity set: ${match[0]} `);
            } else {
              message.warning("Please speak a valid number for Quantity");
            }
          }

          // Before/After Food
          if (transcript.includes("before food") || transcript.includes("beforefood")) {
            setMedicineData(prev => ({ ...prev, timeTaken: "BF" }));
            message.success(`Set to Before Food`);
          }
          if (transcript.includes("after food") || transcript.includes("afterfood")) {
            setMedicineData(prev => ({ ...prev, timeTaken: "AF" }));
            message.success(`Set to After Food`);
          }
          // Notes
          if (transcript.includes("notes")) {
            const note = transcript.replace("notes", "").trim();
            setMedicineData(prev => ({ ...prev, notes: note }));
            message.success(`Notes set: ${note}`);
          }
          else if (transcript.includes("add medicine") || transcript.includes("next medicine")) {
            document.getElementById("addmedicine")?.click();
            message.success(`Medicine Added`);
          }
          if (transcript.includes("edit medicine")) {
            const medicineName = transcript.replace("edit medicine", "").trim().toLowerCase();
            setEditMedicineVoice(medicineName);
            message.success(`Editing medicine: ${medicineName}`);
          }
          if (transcript.includes("update")) {
            document.getElementById("updateMedicineBtn")?.click();
          }
          if (transcript.includes("select all medicine")) {
            setSelectAllVoice("select all medicine");
            message.success("Voice: Select all medicine");
            setMedicineVoice("");
            return;
          }

        }
        // if (currentSection === "addLabTest") {
        //   if (
        //     transcript.startsWith("delete lab test") ||
        //     transcript.startsWith("remove lab test")
        //   ) {
        //     const name = transcript
        //       .replace("delete test", "")
        //       .replace("remove test", "")
        //       .trim();
        //     setDeleteLabTestVoice(name);
        //     message.success(`Delete request for lab test: ${name}`);
        //     return;
        //   }

        //   if (transcript.includes("test name") &&
        //     !transcript.includes("add lab test")) {
        //     transcript = transcript.replace(/[.]/g, "").trim();
        //     const labName = transcript.replace("test name", "").trim();
        //     setLabTestVoice(labName);
        //     message.success(`Lab Test via voice: ${labName}`);
        //   }
        //   else if (transcript.includes("add test")) {
        //     document.getElementById("addlabtest")?.click();
        //     message.success(`Lab Test Added`);
        //   }
        //   if (transcript.includes("select all test")) {
        //     setSelectAllVoice("labtest");
        //     setLabTestVoice(""); // Prevent accidental input
        //     message.success("Voice: Select all lab tests");
        //     return;
        //   }

        // }
        if (currentSection === "addLabTest") {
  const lowerTranscript = transcript.toLowerCase().trim();

  // ✅ Delete Lab Test
  const deleteLabTestTriggers = [
    "delete lab test",
    "remove lab test",
    "delete test",
    "remove test"
  ];
  const testToDelete = getVoiceValueFromTrigger(lowerTranscript, deleteLabTestTriggers);
  if (testToDelete !== null) {
    setDeleteLabTestVoice(testToDelete);
    message.success(`Delete request for lab test: ${testToDelete}`);
    return;
  }

  // ✅ Set Lab Test Name
  const labTestNameTriggers = [
    "test name",
    "lab test name",
    "name of test",
    "test is"
  ];
  const labName = getVoiceValueFromTrigger(lowerTranscript, labTestNameTriggers);
  if (labName !== null && !lowerTranscript.includes("add lab test")) {
    const cleanedName = labName.replace(/[.]/g, "").trim();
    setLabTestVoice(cleanedName);
    message.success(`Lab Test via voice: ${cleanedName}`);
  }

  // ✅ Add Lab Test
  if (
    lowerTranscript.includes("add test") ||
    lowerTranscript.includes("add lab test")
  ) {
    document.getElementById("addlabtest")?.click();
    message.success(`Lab Test Added`);
  }

  // ✅ Select All
  if (
    lowerTranscript.includes("select all test") ||
    lowerTranscript.includes("select all lab test") ||
    lowerTranscript.includes("select all lab tests")
  ) {
    setSelectAllVoice("labtest");
    setLabTestVoice(""); // Prevent accidental input
    message.success("Voice: Select all lab tests");
    return;
  }
}


        if (currentSection === "treatmentPlan") {
          if (transcript.includes("clear treatment plan")) {
            setTreatmentPlanVoice("clear treatment plan"); // Clear the field
            message.success("Treatment Plan cleared");
            return; // Exit early to avoid appending below
          }
        
          if (transcript.includes("treatment plan")) {
            const newText = transcript.replace("treatment plan", "").trim();
        
            // Prevent setting duplicate voice content
            setTreatmentPlanVoice((prev) => {
              if (!prev || !prev.endsWith(newText)) {
                return prev ? `${prev} ${newText}` : newText;
              }
              return prev;
            });
        
            message.success(`Treatment Plan added: ${newText}`);
          }
        }

        else if (transcript.includes("end consultation")) {
          document.getElementById("endConsultation")?.click();
          message.success(`Consultation Ended`);
        }

        else {
          console.warn("Unrecognized command:", transcript);
        }
      }
    };
    if (isListening && !isRecognitionRunningRef.current) {
      try {
        recognition.start();
        message.success("🎙️ Listening...");
      } catch (error) {
        console.warn("🛑 Failed to start recognition:", error);
      }
    }
    return () => {
      recognition.stop();
      // isRecognitionRunningRef = false;
    };
  }, [isListening, recognitionRef.current, location.pathname]);
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        isListening &&
        !isRecognitionRunningRef.current &&
        recognitionRef.current
      ) {
        try {
          recognitionRef.current.start();
          isRecognitionRunningRef.current = true;
        } catch (e) {
          console.warn("⚠️ Failsafe restart failed:", e);
        }
      }
    }, 15000); // every 15 seconds

    return () => clearInterval(interval); // ✅ cleanup on unmount
  }, [isListening, recognitionRef]);


  return null;
};

export default VoiceCommandController;
